/**
 * UI Components - Reusable components
 */

import {
  t,
  getCurrentLanguage,
  translateRoomType,
  translateRatePlan,
  translateRatePlanDescription,
  translateAmenity,
  translateRoomDescription,
  getCustomAmenityIcon,
} from './i18n';
import { getActiveServices } from './api';

/**
 * Language switcher component (dropdown style)
 */
export function LanguageSwitcher(): string {
  const currentLang = getCurrentLanguage();
  const languages = [
    { code: 'th', flag: '🇹🇭', name: 'ภาษาไทย' },
    { code: 'en', flag: '🇬🇧', name: 'English' },
    { code: 'zh', flag: '🇨🇳', name: '中文' },
  ];

  const current = languages.find(l => l.code === currentLang) || languages[0];

  return `
    <div class="lang-dropdown">
      <button class="lang-dropdown-btn" onclick="window.toggleLangDropdown?.()">
        <svg class="globe-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
        <span class="current-flag">${current.flag}</span>
        <svg class="dropdown-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M6 9l6 6 6-6"/>
        </svg>
      </button>
      <div class="lang-dropdown-menu" id="lang-menu">
        ${languages
          .map(
            lang => `
          <button 
            class="lang-option ${currentLang === lang.code ? 'active' : ''}" 
            onclick="window.switchLanguage?.('${lang.code}')"
          >
            <span class="lang-flag">${lang.flag}</span>
            <span class="lang-name">${lang.name}</span>
            ${
              currentLang === lang.code
                ? `
              <svg class="check-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                <path d="M20 6L9 17l-5-5"/>
              </svg>
            `
                : ''
            }
          </button>
        `
          )
          .join('')}
      </div>
    </div>
  `;
}

/**
 * Create header component
 */
export function Header(
  title: string,
  showBack: boolean = false,
  showLangSwitcher: boolean = true
): string {
  const backButton = showBack
    ? `<button class="header-back" onclick="window.handleBack?.()">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
      </button>`
    : '';
  return `
    <header class="header">
      ${backButton}
      <h1 class="header-title">${title}</h1>
      ${showLangSwitcher ? LanguageSwitcher() : '<div class="header-spacer"></div>'}
    </header>
  `;
}

/**
 * Service navigation tabs component (Rooms, Spa, Restaurant)
 * Dynamically hides disabled services, and hides the navigation bar entirely if <= 1 service is active.
 */
export function renderServiceTabs(activePage: 'search' | 'spa' | 'restaurant'): string {
  const lang = getCurrentLanguage();
  const services = getActiveServices();

  const activeCount = (services.rooms ? 1 : 0) + (services.spa ? 1 : 0) + (services.restaurant ? 1 : 0);

  // If 0 or 1 service is active, hide navigation bar completely!
  if (activeCount <= 1) {
    return '';
  }

  const tabLabels = {
    th: { rooms: '🏨 ห้องพัก', spa: '🌿 สปา', restaurant: '🍽️ ร้านอาหาร' },
    en: { rooms: '🏨 Rooms', spa: '🌿 Spa', restaurant: '🍽️ Dining' },
    zh: { rooms: '🏨 客房', spa: '🌿 水疗', restaurant: '🍽️ 餐厅' },
  }[lang] || { rooms: '🏨 Rooms', spa: '🌿 Spa', restaurant: '🍽️ Dining' };

  return `
    <div style="display:flex;background:#fff;border-bottom:1px solid #e0e0e0;position:sticky;top:56px;z-index:90;">
      ${services.rooms ? `
        <button class="nav-service-tab ${activePage === 'search' ? 'active' : ''}" onclick="window.navigateTo?.('search')" style="flex:1;padding:12px;text-align:center;border:none;background:none;font-weight:${activePage === 'search' ? '600' : '500'};color:${activePage === 'search' ? '#00B900' : '#666'};border-bottom:${activePage === 'search' ? '2px solid #00B900' : '2px solid transparent'};cursor:pointer;">
          ${tabLabels.rooms}
        </button>
      ` : ''}
      ${services.spa ? `
        <button class="nav-service-tab ${activePage === 'spa' ? 'active' : ''}" onclick="window.navigateTo?.('spa')" style="flex:1;padding:12px;text-align:center;border:none;background:none;font-weight:${activePage === 'spa' ? '600' : '500'};color:${activePage === 'spa' ? '#00B900' : '#666'};border-bottom:${activePage === 'spa' ? '2px solid #00B900' : '2px solid transparent'};cursor:pointer;">
          ${tabLabels.spa}
        </button>
      ` : ''}
      ${services.restaurant ? `
        <button class="nav-service-tab ${activePage === 'restaurant' ? 'active' : ''}" onclick="window.navigateTo?.('restaurant')" style="flex:1;padding:12px;text-align:center;border:none;background:none;font-weight:${activePage === 'restaurant' ? '600' : '500'};color:${activePage === 'restaurant' ? '#00B900' : '#666'};border-bottom:${activePage === 'restaurant' ? '2px solid #00B900' : '2px solid transparent'};cursor:pointer;">
          ${tabLabels.restaurant}
        </button>
      ` : ''}
    </div>
  `;
}

/**
 * Create loading spinner
 */
export function LoadingSpinner(message?: string): string {
  return `
    <div class="loading-container">
      <div class="spinner"></div>
      <p>${message || t('loading')}</p>
    </div>
  `;
}

/**
 * Create error message
 */
export function ErrorMessage(message: string, onRetry?: () => void): string {
  const retryButton = onRetry
    ? `<button class="btn btn-primary" onclick="window.handleRetry?.()">${t('retry')}</button>`
    : '';

  return `
    <div class="error-container">
      <div class="error-icon">⚠️</div>
      <p class="error-message">${message}</p>
      ${retryButton}
    </div>
  `;
}

/**
 * Hotel Card - displays hotel info at top of search
 * Pass hotelInfo from API or leave empty for loading state
 */
export function HotelCard(hotelInfo?: {
  name: string;
  address: string;
  rating: number;
  image: string;
}): string {
  // If no hotel info, show loading placeholder
  if (!hotelInfo) {
    return `
      <div class="hotel-card hotel-card-loading">
        <div class="hotel-card-content">
          <div class="hotel-card-image skeleton"></div>
          <div class="hotel-card-info">
            <div class="skeleton skeleton-text" style="width: 80%; height: 20px;"></div>
            <div class="skeleton skeleton-text" style="width: 40%; height: 16px; margin-top: 8px;"></div>
            <div class="skeleton skeleton-text" style="width: 60%; height: 14px; margin-top: 8px;"></div>
          </div>
        </div>
      </div>
    `;
  }

  const stars = '⭐'.repeat(hotelInfo.rating);

  return `
    <div class="hotel-card">
      <div class="hotel-card-content">
        <div class="hotel-card-image">
          <img src="${hotelInfo.image}" alt="${hotelInfo.name}" loading="lazy" />
        </div>
        <div class="hotel-card-info">
          <h2 class="hotel-card-name">${hotelInfo.name}</h2>
          <div class="hotel-card-rating">${stars}</div>
          <p class="hotel-card-address">📍 ${hotelInfo.address}</p>
        </div>
      </div>
    </div>
  `;
}

/**
 * Step indicator for booking flow
 */
export function StepIndicator(currentStep: number): string {
  const steps = [
    { num: 1, label: t('stepSearch'), icon: '🔍' },
    { num: 2, label: t('stepRoom'), icon: '🛏️' },
    { num: 3, label: t('stepGuest'), icon: '👤' },
    { num: 4, label: t('stepConfirm'), icon: '✓' },
  ];

  return `
    <div class="step-indicator">
      ${steps
        .map((step, index) => {
          const isCompleted = currentStep > step.num;
          const isActive = currentStep === step.num;
          const lineCompleted = currentStep > step.num; // Line is green if this step is completed

          return `
          <div class="step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}">
            <div class="step-circle">
              ${
                isCompleted
                  ? `
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
              `
                  : step.num
              }
            </div>
            <span class="step-label">${step.label}</span>
          </div>
          ${index < steps.length - 1 ? `<div class="step-line ${lineCompleted ? 'completed' : ''}"></div>` : ''}
        `;
        })
        .join('')}
    </div>
  `;
}

/**
 * Floating footer with Back / Forward buttons
 */
export function FloatingNav(disableNext: boolean = false): string {
  return `
    <nav class="floating-footer" role="navigation" aria-label="Footer Navigation">
      <div class="floating-footer-inner">
        <button 
          class="floating-circle floating-back" 
          onclick="(function(){ try{ if(window.handleBack) { window.handleBack(); return; } history.back(); }catch(e){ console.error(e); } })()" 
          title="${t('back')}">
          ◀
        </button>
        <button 
          class="floating-circle floating-forward" 
          ${disableNext ? 'disabled' : ''}
          onclick="(function(){ try{ if(window.handleForward) { window.handleForward(); return; } var btn = document.querySelector('.btn.btn-primary, .btn-primary, button[type=submit]'); if (btn) { btn.click(); return; } }catch(e){ console.error(e); } })()" 
          title="${t('next')}">
          ▶
        </button>
      </div>
    </nav>
  `;
}

/**
 * Create button
 */
export function Button(
  text: string,
  onClick: string,
  variant: 'primary' | 'secondary' | 'outline' = 'primary',
  disabled: boolean = false
): string {
  return `
    <button 
      class="btn btn-${variant}" 
      onclick="${onClick}"
      ${disabled ? 'disabled' : ''}
    >
      ${text}
    </button>
  `;
}

/**
 * Create input field
 */
export function InputField(
  label: string,
  name: string,
  type: string = 'text',
  value: string = '',
  required: boolean = false,
  placeholder: string = ''
): string {
  return `
    <div class="form-group">
      <label class="form-label" for="${name}">
        ${label}
        ${required ? '<span class="required">*</span>' : ''}
      </label>
      <input 
        type="${type}"
        id="${name}"
        name="${name}"
        class="form-input"
        value="${value}"
        placeholder="${placeholder}"
        ${required ? 'required' : ''}
      />
      <span class="field-error" id="${name}-error"></span>
    </div>
  `;
}

/**
 * Create select field
 */
export function SelectField(
  label: string,
  name: string,
  options: { value: string; text: string }[],
  selected: string = '',
  required: boolean = false
): string {
  const optionsHtml = options
    .map(
      opt =>
        `<option value="${opt.value}" ${opt.value === selected ? 'selected' : ''}>${opt.text}</option>`
    )
    .join('');

  return `
    <div class="form-group">
      <label class="form-label" for="${name}">
        ${label}
        ${required ? '<span class="required">*</span>' : ''}
      </label>
      <select id="${name}" name="${name}" class="form-select" ${required ? 'required' : ''}>
        ${optionsHtml}
      </select>
    </div>
  `;
}

/**
 * Create textarea
 */
export function TextArea(
  label: string,
  name: string,
  value: string = '',
  placeholder: string = '',
  rows: number = 3
): string {
  return `
    <div class="form-group">
      <label class="form-label" for="${name}">${label}</label>
      <textarea 
        id="${name}"
        name="${name}"
        class="form-textarea"
        placeholder="${placeholder}"
        rows="${rows}"
      >${value}</textarea>
    </div>
  `;
}

/**
 * Create card component
 */
export function Card(content: string, className: string = ''): string {
  return `
    <div class="card ${className}">
      ${content}
    </div>
  `;
}

/**
 * Create room card
 */
export function RoomCard(
  room: {
    roomTypeCode: string;
    roomTypeName: string;
    ratePlanCode: string;
    ratePlanName: string;
    totalRate: number;
    ratePerRoom: number;
    averageNightlyRate: number;
    numberOfRooms: number;
    currency: string;
    available: number;
    description?: string;
    bedType?: string;
    bedTypeDesc?: string;
    amenities?: string[];
    cancellationPolicy?: string;
    mealPlan?: string;
    isRefundable?: boolean;
  },
  nights: number,
  onSelect: string
): string {
  // Get translated names
  const roomTypeName = translateRoomType(room.roomTypeCode, room.roomTypeName);
  const ratePlanName = translateRatePlan(room.ratePlanCode, room.ratePlanName);
  const mealPlanDesc = translateRatePlanDescription(room.ratePlanCode, room.mealPlan);

  // Translate room description
  const translatedDescription = translateRoomDescription(room.roomTypeCode, room.description);

  // Room description
  const descriptionHtml = translatedDescription
    ? `<p class="room-description">${translatedDescription}</p>`
    : '';

  // Amenities with icons
  const amenityIcons: Record<string, string> = {
    WIFI: '📶',
    TV: '📺',
    MINIBAR: '🍷',
    SAFE: '🔒',
    AC: '❄️',
    HAIRDRYER: '💨',
    BATHTUB: '🛁',
    SOFA: '🛋️',
    JACUZZI: '♨️',
    LIVING_ROOM: '🏠',
    BALCONY: '🏖️',
    DESK: '💼',
    LOUNGE_ACCESS: '👔',
  };

  const amenitiesHtml = room.amenities?.length
    ? `<div class="room-amenities">
        ${room.amenities
          .slice(0, 6)
          .map(code => {
            const icon = getCustomAmenityIcon(code) || amenityIcons[code] || '✓';
            const label = translateAmenity(code);
            return `<span class="amenity-item" title="${label}">${icon} ${label}</span>`;
          })
          .join('')}
       </div>`
    : '';

  return `
    <div class="room-card">
      <div class="room-header">
        <h3 class="room-name">${roomTypeName}</h3>
        <span class="room-available">${room.available} ${t('rooms')}</span>
      </div>
      ${room.bedType ? `<div class="room-bed-type" style="font-size:12px; color:#555; font-weight:600; margin-top:2px; margin-bottom:6px;">🛏️ ${room.bedType}${room.bedTypeDesc ? ` (${room.bedTypeDesc})` : ''}</div>` : ''}
      ${descriptionHtml}
      ${amenitiesHtml}
      ${room.cancellationPolicy ? `<p class="room-policy"><span class="icon">📋</span> ${room.cancellationPolicy}</p>` : ''}

      <!-- Room images carousel placeholder -->
      <div class="room-images" data-room-type="${room.roomTypeCode}"></div>
      <div class="room-pricing">
        <div class="price-nightly">
          <span class="price-amount">${formatPrice(room.averageNightlyRate, room.currency)}<sup>++</sup></span>
          <span class="price-unit">${t('perNight')}</span>
        </div>
        <div class="price-note">${t('excludingTaxes')}</div>
        <div class="price-total">
          ${
            room.numberOfRooms > 1
              ? `${room.numberOfRooms} ${t('rooms')} × ${nights} ${t('nights')}: <strong>${formatPrice(room.totalRate, room.currency)}</strong> <span class="tax-note">${t('inclTaxService')}</span>`
              : `${t('totalFor')} ${nights} ${t('nights')}: <strong>${formatPrice(room.totalRate, room.currency)}</strong> <span class="tax-note">${t('inclTaxService')}</span>`
          }
        </div>
      </div>
      <div class="room-rate-details">
        <span class="rate-plan-name">${ratePlanName}</span>${mealPlanDesc ? ` - <span class="rate-plan-desc">${mealPlanDesc}</span>` : ''}${room.isRefundable === false ? ` <span style="display:inline-block; background:#dc2626; color:#fff; font-size:11px; font-weight:600; padding:1px 7px; border-radius:4px; margin-left:4px;">${t('nonRefundable')}</span>` : ''}
      </div>
      <button class="btn btn-primary btn-block" onclick="${onSelect}">
        ${t('selectRoom')}
      </button>
    </div>
  `;
}

/**
 * Format price with currency
 */
function formatPrice(amount: number, currency: string): string {
  return new Intl.NumberFormat('th-TH', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(amount);
}

/**
 * Create summary row
 */
export function SummaryRow(label: string, value: string, highlight: boolean = false): string {
  return `
    <div class="summary-row ${highlight ? 'highlight' : ''}">
      <span class="summary-label">${label}</span>
      <span class="summary-value">${value}</span>
    </div>
  `;
}

/**
 * Extra charge confirmation modal
 */
export function ExtraChargeModal(
  extraAdults: number,
  extraChildren: number,
  extraAdultCharge: number,
  extraChildCharge: number,
  totalExtraCharge: number,
  currency: string,
  _onConfirm: () => void,
  _onCancel: () => void
): string {
  const formatMoney = (amount: number) => formatPrice(amount, currency);

  return `
    <div class="modal-overlay" id="extra-charge-modal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>⚠️ ${t('capacityExceeded') || 'จำนวนผู้เข้าพักเกินกำหนด'}</h3>
        </div>
        <div class="modal-body">
          <p style="margin-bottom: 16px;">${t('capacityExceededMessage') || 'จำนวนผู้เข้าพักของคุณเกินขีดจำกัดของห้องพัก คุณต้องการชำระค่าบริการเพิ่มหรือไม่?'}</p>
          
          <div class="extra-charges-detail">
            ${
              extraAdults > 0
                ? `
              <div class="charge-row">
                <span>${t('extraAdults') || 'ผู้ใหญ่เพิ่ม'}: ${extraAdults} ${t('person') || 'คน'}</span>
                <span>${formatMoney(extraAdultCharge)} / ${t('personPerNight') || 'คน/คืน'}</span>
              </div>
            `
                : ''
            }
            ${
              extraChildren > 0
                ? `
              <div class="charge-row">
                <span>${t('extraChildren') || 'เด็กเพิ่ม'}: ${extraChildren} ${t('person') || 'คน'}</span>
                <span>${formatMoney(extraChildCharge)} / ${t('personPerNight') || 'คน/คืน'}</span>
              </div>
            `
                : ''
            }
            <div class="charge-row total">
              <strong>${t('totalExtraCharge') || 'ค่าบริการเพิ่มทั้งหมด'}</strong>
              <strong>${formatMoney(totalExtraCharge)}</strong>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" onclick="window.handleExtraChargeCancel?.()">${t('cancel') || 'ยกเลิก'}</button>
          <button class="btn btn-primary" onclick="window.handleExtraChargeConfirm?.()">${t('confirm') || 'ยืนยัน'}</button>
        </div>
      </div>
    </div>
  `;
}
